import React from "react";
import type { FC } from "react";
import {
  Body,
  Box,
  Flex,
  FlexRow,
  Heading,
  Money as MoneyComponent,
} from "@edreamsodigeo/prisma-design-system";
import { useBrand } from "@edreamsodigeo/frontend-core";

import PrimeLogo from "~/components/common/PrimeLogo";
import type { FareCardHeaderProps } from "~/components/common/FareCard/contract";
import {
  getNoButtonFareCardPriceStyle,
  getPriceStyleWithDiscount,
  neutralColor0Hex,
  opacity,
  reColorLogo,
} from "~/components/common/FareCard/styles/styles";

export const FareCardHeaderCTANoButton: FC<FareCardHeaderProps> = ({
  title,
  price,
  discountedPrice,
  type,
  priceDescription,
  subtitle,
  isAutoRenewalPolicy,
}) => {
  const brand = useBrand();
  const isPrimary = type === "primary";

  const priceStyle = getNoButtonFareCardPriceStyle(isPrimary);
  const priceStyleWithDiscount = getPriceStyleWithDiscount();

  return (
    <Flex flexDirection={"column"} gap={"2"}>
      <Flex flexDirection={"column"} gap={"1"} alignItems={"flex-start"}>
        <Body
          type={"base"}
          color={isPrimary ? "neutral.0" : "neutral.90"}
          weight={"medium"}
          sx={{ lineHeight: "24px" }}
        >
          {title}
        </Body>

        <Box
          marginTop={"minus_2x_small"}
          sx={isAutoRenewalPolicy ? { width: "100%" } : undefined}
        >
          {subtitle && (
            <Body
              type={isAutoRenewalPolicy ? "xxSmall" : "xSmall"}
              color={"primary.20"}
              sx={{
                letterSpacing: "0",
                opacity,
                lineHeight: "14px",
              }}
            >
              {subtitle}
            </Body>
          )}
        </Box>
      </Flex>
      <FlexRow
        justifyContent={isPrimary ? "space-between" : "flex-end"}
        marginBottom={"small"}
      >
        {isPrimary && (
          <Flex alignSelf={"end"}>
            <Box
              marginTop={"1x_small"}
              sx={{
                ...reColorLogo[brand]?.sx,
              }}
            >
              <PrimeLogo
                negative={reColorLogo[brand]?.negative || true}
                size={"xs"}
                type={reColorLogo[brand]?.type || "short"}
              />
            </Box>
          </Flex>
        )}
        <Flex
          marginTop={(isPrimary && "minus_1x_small") || undefined}
          flexDirection={"column"}
          gap={"2x_small"}
          alignItems={"flex-end"}
        >
          <Flex flexDirection={"row"} gap={"2x_small"}>
            {discountedPrice && (
              <Flex alignSelf={"end"} marginBottom={"2x_small"}>
                <Body type={"xSmall"}>
                  <MoneyComponent
                    currency={price.currency}
                    strikeThrough={"default"}
                    sx={{
                      ".money-integer": {
                        ...priceStyleWithDiscount,
                      },
                      ".money-fraction": {
                        ...priceStyleWithDiscount,
                      },
                      ".money-currency": {
                        color: neutralColor0Hex,
                        opacity,
                        fontSize: ["12px"],
                      },
                    }}
                    rounded
                  >
                    {price.amount}
                  </MoneyComponent>
                </Body>
              </Flex>
            )}

            <Flex>
              <Heading
                type={"2"}
                weight={"medium"}
                sx={priceStyle.sx}
                color={"neutral.0"}
                textAlign={"right"}
              >
                <MoneyComponent
                  currency={(discountedPrice ?? price).currency}
                  rounded
                >
                  {(discountedPrice ?? price).amount}
                </MoneyComponent>
              </Heading>
            </Flex>
          </Flex>
          {priceDescription && (
            <Box marginTop={"minus_small"}>
              <Body
                type={"xSmall"}
                color={isPrimary ? "neutral.0" : "neutral.70"}
                sx={{
                  fontStyle: "italic",
                  opacity,
                  lineHeight: "18px",
                }}
              >
                {priceDescription}
              </Body>
            </Box>
          )}
        </Flex>
      </FlexRow>
    </Flex>
  );
};
